import { forwardRef, ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { hapticLight } from '@/utils/haptic';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ElementType;
  /** Required aria-label for accessibility */
  label: string;
  size?: 'sm' | 'default' | 'lg';
  variant?: 'default' | 'ghost' | 'destructive';
  /** Disable haptic feedback */
  noHaptic?: boolean;
}

const sizeClasses = {
  sm: 'h-9 w-9',
  default: 'h-11 w-11',
  lg: 'h-12 w-12',
};

const iconSizes = {
  sm: 'w-4 h-4',
  default: 'w-5 h-5',
  lg: 'w-6 h-6',
};

const variantClasses = {
  default: 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white',
  ghost: 'text-gray-500 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300',
  destructive: 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon: Icon, label, size = 'default', variant = 'default', className, onClick, noHaptic, disabled, ...props }, ref) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!noHaptic && !disabled) {
        hapticLight();
      }
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        onClick={handleClick}
        disabled={disabled}
        className={cn(
          'inline-flex items-center justify-center rounded-lg transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'touch-target touch-feedback',
          sizeClasses[size],
          variantClasses[variant],
          className
        )}
        {...props}
      >
        <Icon className={iconSizes[size]} aria-hidden="true" />
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
